import { LogOut, RefreshCw, ShieldOff, WifiOff } from "lucide-react";
import { Button, Card, CardContent, Text } from "@athena/ui";

/**
 * Full-page states for "signed in, but you still can't use this".
 *
 * These exist because the alternative isn't a blank page — it's a *plausible* page.
 * When the API was unreachable the admin shell rendered with no name, no
 * organizations and disabled buttons, which reads as a permissions problem and sent
 * us hunting the wrong thing. An empty list and a failed request must never look
 * the same.
 */
export type AccessStateKind = "offline" | "no-access";

const COPY: Record<AccessStateKind, { title: string; body: string; Icon: typeof WifiOff }> = {
    offline: {
        title: "Can't reach the server",
        body: "You're signed in, but the app couldn't load your account. This is usually a connection problem or the API being down — nothing is wrong with your account.",
        Icon: WifiOff,
    },
    "no-access": {
        title: "You don't have access",
        body: "You're signed in, but this account isn't set up to use the admin app. Ask an administrator to grant you access, then sign in again.",
        Icon: ShieldOff,
    },
};

export function AccessState({
    kind,
    detail,
    onRetry,
    onSignOut,
}: {
    kind: AccessStateKind;
    /** The underlying error, when there is one worth showing. */
    detail?: string;
    onRetry?: () => void;
    onSignOut?: () => void;
}) {
    const { title, body, Icon } = COPY[kind];
    return (
        <div className="flex min-h-screen items-center justify-center p-6">
            <Card width="full" className="max-w-lg">
                <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                    <Icon className="text-muted-foreground h-8 w-8" />
                    <Text weight="semibold" className="text-base">
                        {title}
                    </Text>
                    <Text className="text-muted-foreground text-sm">{body}</Text>
                    {detail ? <Text className="text-muted-foreground text-xs break-words">{detail}</Text> : null}
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
                        {onRetry ? (
                            <Button className="w-auto" onClick={onRetry}>
                                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                                Try again
                            </Button>
                        ) : null}
                        {onSignOut ? (
                            <Button variant="outline" className="w-auto" onClick={onSignOut}>
                                <LogOut className="mr-1.5 h-3.5 w-3.5" />
                                Sign out
                            </Button>
                        ) : null}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
